class KlimGallery extends HTMLElement {
  constructor() {
    super();
    this.items = [];
    this.loadMoreButton = null;
    this.visibleCount = 3;
    this.totalCount = 0;
    this.desktopMinimum = 3;
    // bind once so we can add/remove the listener
    this._onResize = this._debounce(() => this._applyResponsiveRules(), 150);
  }

  connectedCallback() {
    if (this.dataset.init !== 'true') {
      return;
    }
    this.items = Array.from(this.querySelectorAll('.slide:not(.slide--inactive-option)'));
    //console.log(this.items)
    this.totalCount = this.items.length;
    this.loadMoreButton = this.querySelector('[data-load-more]');

    if (this.loadMoreButton) {
      // start hidden; component controls when to show it
      this.loadMoreButton.style.display = 'none';
      this.loadMoreButton.dataset.loadMore = 'false';
      this.loadMoreButton.addEventListener('click', () => this.showAllSlides());
    }

    // Initial responsive pass + render
    this._applyResponsiveRules();
    this.updateGallery();

    // Listen for viewport changes
    window.addEventListener('resize', this._onResize, { passive: true });
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this._onResize);
    if (this.loadMoreButton) {
      this.loadMoreButton.removeEventListener('click', () => this.showAllSlides());
    }
  }

  showAllSlides() {
    this.visibleCount = this.totalCount;
    this.updateGallery();
  }

  updateGallery() {
    const isMobile = window.innerWidth < 768;

    // On small screens, never hide slides
    const maxVisible = isMobile ? this.totalCount : this.visibleCount;
    const items = Array.from(this.querySelectorAll('.slide:not(.slide--inactive-option)'));
    const revealed = [];
    items.forEach((item, i) => {
      const show = i < maxVisible;
      if (show && item.style.display === 'none') {
        revealed.push(item);
      }
      item.style.display = show ? '' : 'none';
    });

    if (revealed.length) {
      this._loadRevealedImages(revealed);
    }

    // Show Load More only when desktop/tablet (>=768) AND there are hidden slides
    if (this.loadMoreButton) {
      const shouldShow =
        !isMobile && this.totalCount > this.visibleCount;
      this.loadMoreButton.style.display = shouldShow ? '' : 'none';
      this.loadMoreButton.dataset.loadMore = shouldShow ? 'true' : 'false';
    }

    // Notify listeners about total slides
    this.dispatchEvent(
      new CustomEvent('galleryUpdated', {
        detail: { totalSlides: this.totalCount },
      })
    );
  }

  refresh() {
    if (this.dataset.init !== 'true') {
      return;
    }
    this.items = Array.from(this.querySelectorAll('.slide:not(.slide--inactive-option)'));
    this.totalCount = this.items.length;
    // Keep current visibleCount but cap to total; small screens will override in update
    this.visibleCount = Math.min(this.desktopMinimum, this.totalCount);

    this._applyResponsiveRules();
    this.updateGallery();
  }

  /**
   * Slides start hidden with display:none, so their images are skipped by the
   * theme's lazy loader (no box to measure) and by native loading="lazy"
   * (never intersects). Once a slide is revealed, make sure its image really
   * loads and is shown instead of relying on observers to catch up.
   */
  _loadRevealedImages(slides) {
    const imgs = [];
    slides.forEach((slide) => {
      slide.querySelectorAll('img').forEach((img) => imgs.push(img));
    });
    if (!imgs.length) return;

    // 1. Loader skipped these while hidden: run it now that they have a box.
    if (window.theme && theme.lazy && imgs.some((img) => img.dataset.src)) {
      theme.lazy.loadSrcSets(true);
    }

    imgs.forEach((img) => {
      // 2. Revealed by a click: fetch now rather than waiting for native lazy loading.
      if (img.loading === 'lazy') {
        img.loading = 'eager';
      }
      // 3. Already decoded (e.g. from cache) but never marked as loaded: unhide it.
      const reveal = () => {
        if (img.naturalWidth > 1) img.classList.add('loaded');
      };
      if (img.complete) {
        reveal();
      } else {
        img.addEventListener('load', reveal, { once: true });
      }
    });
  }

  /** Ensure visibleCount matches responsive rule before updating UI */
  _applyResponsiveRules() {
    const isMobile = window.innerWidth < 768;
    if (isMobile) {
      // force “show all” on small screens
      this.visibleCount = this.totalCount;
    } else {
      // on larger screens, keep at least 4 visible
      this.visibleCount = Math.max(3, Math.min(this.visibleCount, this.totalCount));
    }
  }

  /** Tiny debounce helper */
  _debounce(fn, wait = 150) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

customElements.define('klim-gallery', KlimGallery);
